import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../../services/api';
import { RootState } from '../rootReducer';
import { IAccount } from './account';
import {
  fetchAccountsList
} from './list.thunks';
import { createPaymentAndBilling, IArgsPaymentAndBilling } from './paymentAndBilling/paymentAndBilling.thunk';
import { Features, isFeatureEnabled } from '../../featureToggles/features';
import { AccessTypes } from '../../components/common/domain/accountAccessType';
import { startCancellationProcessForPlgExternal2 } from '../customer/customer.thunk';
import { getCurrentRenewal } from './currentRenewal/currentRenewal.thunk';
import { fetchHubspotCompany } from './company/thunk';
import { updateUserStatusById } from '../settings/userStatus.thunk';
import { startEnterpriseTrial } from '../packagesInfo/index.thunk';
import { IUpdateBillingArgs, updateBillingAddress } from './updateBillingAddress/updateBillingAddress.thunk';
import { updateQuestionPackages } from '../questionPackages/form.thunks';

export interface AccountListState {
  accounts: IAccount[];
  totalCount: number;
  unsorted: IAccount[];
  loading: boolean;
  error: ApiError | null;
  editingAccount: string;
  isDirty: boolean;
}

const initialState: AccountListState = {
  error: null,
  loading: false,
  accounts: [],
  totalCount: 0,
  unsorted: [],
  editingAccount: '',
  isDirty: false
};

const getAccountListState = (state:RootState):AccountListState => state.accounts.list;
const getError = (state:RootState):ApiError | null => getAccountListState(state).error;
const getList = (state:RootState):IAccount[] => getAccountListState(state).accounts;
const getTotalCount = (state:RootState):number => getAccountListState(state).totalCount;
const getUnsortedList = (state:RootState):IAccount[] => getAccountListState(state).unsorted;
const getLoading = (state:RootState):boolean => getAccountListState(state).loading;
const getEditingAccountId = (state:RootState):string => getAccountListState(state).editingAccount;
const getIsDirty = (state: RootState): boolean => getAccountListState(state).isDirty;

const getAccountById = (state:RootState, accountId: string):IAccount | null => {
  const accounts: IAccount[] = getList(state);
  const account: IAccount | null = accounts.find((account) => account.id === accountId) || null;
  return account ;
};

const isLiteAccount = (state: RootState, accountId: string):boolean => {
  if (!accountId) {
    return false;
  }
  const account = getAccountById(state, accountId);
  return account?.accessType === AccessTypes.Lite;
};

const isEnterpriseAccount = (state: RootState, accountId: string):boolean => {
  if (!accountId) {
    return false;
  }
  const account = getAccountById(state, accountId);
  return account?.accessType === AccessTypes.Enterprise;
};

export const selectors = {
  getError,
  getList,
  getTotalCount,
  getUnsortedList,
  getLoading,
  getEditingAccountId,
  getAccountById,
  isLiteAccount,
  isEnterpriseAccount,
  getIsDirty
};

export type UpdateListPayload = {
  newList: IAccount[];
}

export type UpdateTableQueryPayload = string;

const accountsListSlice = createSlice({
  name: 'accountsList',
  initialState,
  reducers: {
    setEditingAccount: (state:AccountListState, action: PayloadAction<string>) => {
      state.editingAccount = action.payload;
    },
    clearEditingAccount: (state:AccountListState) => {
      state.editingAccount = '';
    },
    updateAccount: (state:AccountListState, action:PayloadAction<IAccount>) => {
      const updateAccount = action.payload;
      state.unsorted = state.unsorted.map(account => account.id === updateAccount.id ? { ...account, ...updateAccount } : account);
      state.accounts = [...state.unsorted];
    },
    updateAccountBillingInfo: (state:AccountListState, action:PayloadAction<IArgsPaymentAndBilling>) => {
      const { accountId, ...billingInfo } = action.payload;
      state.unsorted = state.unsorted.map(account => account.id === accountId ? { ...account, ...billingInfo } : account);
      state.accounts = [...state.unsorted];
    },
    updateAccountBillingAddress: (state:AccountListState, action:PayloadAction<IUpdateBillingArgs>) => {
      const { accountId, ...billingInfo } = action.payload;
      state.unsorted = state.unsorted.map(account => account.id === accountId ? { ...account, ...billingInfo } : account);
      state.accounts = [...state.unsorted];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAccountsList.pending, (state) => {
      state.loading = true;
      state.accounts = [];
      state.totalCount = 0;
      state.unsorted = [];
    });
    builder.addCase(fetchAccountsList.fulfilled, (state, action: AnyAction) => {
      state.loading = false;
      // TODO: the count error comes from old code. Here failing due to refactor. And fixed by manually set action to AnyAction
      state.totalCount = action.payload.count ?? (action.payload.accounts ? action.payload.accounts.length : 0 );
      state.accounts = action.payload.accounts;
      state.unsorted = [...(action.payload.accounts || [])];
      state.isDirty = false;
    });
    builder.addCase(fetchAccountsList.rejected, (state, action: AnyAction) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createPaymentAndBilling.fulfilled, (state, action) => {
      const { meta: { arg: { accountId, billingAddress } } } = action;
      const accountIndex = state.accounts.findIndex((account) => account.id === accountId );
      if (billingAddress) {
        state.accounts[accountIndex].billingAddress = billingAddress;
      }
    });
    builder.addCase(updateBillingAddress.fulfilled, (state, action) => {
      const { meta: { arg: { accountId, billingAddress } } } = action;
      const accountIndex = state.accounts.findIndex((account) => account.id === accountId );
      if (billingAddress) {
        state.accounts[accountIndex].billingAddress = billingAddress;
      }
    });
    builder.addCase(startCancellationProcessForPlgExternal2.fulfilled.type, (state, action: PayloadAction<void, string, { arg: string }>): void => {
      const { meta: { arg: accountId } } = action;
      const account = state.accounts.find((account) => account.id === accountId);
      if (account?.subscription) {
        account.subscription.cancellationInProgress = true;
      }

      const unsortedAccount = state.unsorted.find((account) => account.id === accountId);
      if (unsortedAccount?.subscription) {
        unsortedAccount.subscription.cancellationInProgress = true;
      }
    });
    builder.addCase(getCurrentRenewal.fulfilled, (state, action) => {
      const { meta: { arg: { accountId } }, payload } = action;
      if (!payload) {
        return;
      }
      const { currentDeal } = payload;
      const accountIndex = state.accounts.findIndex((account) => account.id === accountId );
      if (state.accounts[accountIndex]) {
        state.accounts[accountIndex].currentDeal = currentDeal;
      }
    });
    builder.addCase(getCurrentRenewal.rejected, (state, action) => {
      const { meta: { arg: { accountId } }, payload } = action;
      if (!payload) {
        return;
      }
      const accountIndex = state.accounts.findIndex((account) => account.id === accountId );
      if (state.accounts[accountIndex]) {
        state.accounts[accountIndex].currentDeal = null;
      }
    });
    builder.addCase(fetchHubspotCompany.fulfilled, (state, action) => {
      const { meta: { arg: { accountId } }, payload } = action;
      if (!payload) {
        return;
      }
      const { renewalDate } = payload;
      const accountIndex = state.accounts.findIndex((account) => account.id === accountId );
      if (state.accounts[accountIndex]) {
        state.accounts[accountIndex].contractRenewalDate = renewalDate;
      }
    });
    builder.addCase(updateUserStatusById.pending, (state) => {
      state.isDirty = true;
    });
    builder.addCase(startEnterpriseTrial.fulfilled, (state) => {
      if (isFeatureEnabled(Features.TrialsOfTiers)) {
        state.isDirty = true;
      }
    });
    builder.addCase(updateQuestionPackages.fulfilled, (state, action: PayloadAction<void, string, { arg: { accountId: string; questionsPackages: string[] } }>) => {
      if (!isFeatureEnabled(Features.QlNetquest)) {
        return;
      }
      const { meta: { arg: { accountId, questionsPackages } } } = action;
      const account = state.accounts.find((account) => account.id === accountId);
      if (!account) {
        return;
      }
      account.questionsPackages = questionsPackages;
    });
  }
});

export default accountsListSlice.reducer;
export const actions = {
  ...accountsListSlice.actions,
  fetchAccountsList
};
